// Ящик профиля (Design Decision 3) — оверлей поверх рабочей области, не переключает активный
// продукт/URL. Ящик рисует сам продукт, ядро только держит контейнер + единую точку открытия.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadModule } from "../mount/moduleLoader";
import { productTextApi } from "../i18n/productDict";
import { createOsnovaContext, sharedEventBus } from "../osnovaContext";
// Канон платформы: появление/уход содержимого — 350мс (module-pane, kit pp-panel).
const PROFILE_STUB_CLOSE_MS = 350;
export function useProfileDrawer({ products, installedProducts, theme, locale, numFmt, user, navigate, openModal }) {
    const [profileOpen, setProfileOpen] = useState(false);
    // Вкладка ящика (general/absence/settings) — приходит кликом по подпункту «Мой профиль»
    // в меню продукта-владельца (см. useProductMenu.pickItem), доставляется продукту через ctx.view.
    const [profileTab, setProfileTab] = useState("general");
    const [profileClosing, setProfileClosing] = useState(false);
    const profileOpenRef = useRef(false);
    useEffect(() => { profileOpenRef.current = profileOpen; }, [profileOpen]);
    const profileHostRef = useRef(null);
    const profileMountedRef = useRef(null);
    // Анимацию выезда рисует продукт — ядро лишь просит его закрыться (registerCloseRequest).
    const profileCloseRequestRef = useRef(null);
    const profileClosingRef = useRef(false);
    // Запрос на закрытие ящика профиля — если продукт зарегистрировал свою функцию (проиграет
    // анимацию выезда и сам вызовет onClose), иначе Оболочка сама играет 350мс канон-переход
    // на своей системной заглушке (нет продукта-владельца) и только потом снимает контейнер.
    const requestProfileClose = useCallback(() => {
        if (profileClosingRef.current)
            return; // анимация уже идёт — повторный запрос игнорируем
        const fn = profileCloseRequestRef.current;
        if (fn) {
            profileClosingRef.current = true;
            fn();
            return;
        }
        profileClosingRef.current = true;
        setProfileClosing(true);
        window.setTimeout(() => {
            setProfileOpen(false);
            setProfileClosing(false);
            profileClosingRef.current = false;
        }, PROFILE_STUB_CLOSE_MS);
    }, []);
    // Единая точка открытия ящика профиля — вызывается аватаром шапки И любым экраном продукта
    // через sharedEventBus (ctx.events.emit("platform:open-profile")), одна дверца на оба пути.
    const openProfile = useCallback(() => {
        if (profileOpenRef.current) {
            requestProfileClose();
            return;
        }
        setProfileOpen(true);
    }, [requestProfileClose]);
    // Клик по «Мой профиль»/её вкладке в меню продукта (useProductMenu.pickItem) шлёт tab —
    // ящик открывается (не тоглится) сразу на нужной вкладке; аватар шапки шлёт событие без detail.
    useEffect(() => sharedEventBus.on("platform:open-profile", (data) => {
        const tab = data?.tab;
        if (tab) {
            setProfileTab(tab);
            setProfileOpen(true);
            return;
        }
        openProfile();
    }), [openProfile]);
    // Ящик профиля — закрытие по Esc (та же механика, референс PlatformProfilePanel).
    useEffect(() => {
        if (!profileOpen)
            return;
        const onKey = (e) => { if (e.key === "Escape")
            requestProfileClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [profileOpen, requestProfileClose]);
    // Клик по обычному пункту меню продукта (useProductMenu.pickItem) эмитит platform:section
    // тем же сигналом, что получает экран продукта под ящиком — ящик, открытый поверх экрана,
    // слушает то же событие и закрывается (та же механика, что Esc), не дожидаясь отдельного
    // события: иначе экран под ящиком меняется (или не меняется при совпавшем разделе), а ящик
    // остаётся поверх и клик выглядит так, будто «ничего не открылось» (тикет #90).
    useEffect(() => {
        if (!profileOpen)
            return;
        return sharedEventBus.on("platform:section", () => requestProfileClose());
    }, [profileOpen, requestProfileClose]);
    // Запись профиля продукта (если паспорт её объявил) — аватар открывает её оверлеем,
    // не переключая активный продукт; нет записи → системная заглушка (яруса account_home нет).
    const profileEntry = products.find((p) => p.role === "profile") ?? null;
    // Контекст для монтирования ящика — отдельный от активного продукта: свои onSections/
    // onSectionChange/onCrumbs — заглушки (ящик не управляет рельсом), nav/modal — настоящие
    // (продукту профиля может понадобиться открыть карточку сотрудника или модалку).
    const profileCtx = useMemo(() => {
        if (!profileEntry)
            return null;
        return {
            ...createOsnovaContext({
                module: profileEntry.product,
                view: profileTab,
                theme, locale, numFmt,
                user: user ? { id: user.userId, email: user.email, displayName: user.displayName, isOwner: user.isOwner } : { id: "", email: "" },
                permissions: user?.perms ?? [],
                navigate,
                setBreadcrumbs: () => { },
                openModal,
                emitter: sharedEventBus,
                section: profileEntry.section ?? null,
                onSections: () => { },
                // Смена вкладки внутри ящика (клик по табам продукта-профиля) — двигает подсветку
                // подпункта в меню продукта-владельца, не переключая раздел самого продукта.
                onSectionChange: (id) => setProfileTab(id),
                onCrumbs: () => { },
                installedProducts,
                productText: productTextApi,
            }),
            onClose: () => setProfileOpen(false),
            registerCloseRequest: (fn) => { profileCloseRequestRef.current = fn; },
        };
    }, [profileEntry, profileTab, theme, locale, numFmt, user, navigate, openModal, installedProducts]);
    // Монтирование/размонтирование ящика профиля — динамический import того же /m/<product>/
    // entry.js (loadModule кеширует после первого раза), закрытие любым из трёх способов
    // (крестик/Esc/повторный клик по аватару) сходится в profileOpen=false — unmount + снятие пейна.
    useEffect(() => {
        const host = profileHostRef.current;
        if (!profileOpen || !host || !profileCtx) {
            const mounted = profileMountedRef.current;
            if (mounted) {
                try {
                    mounted.mod.unmount(mounted.el);
                }
                catch { /* игнор */ }
                mounted.el.remove();
                profileMountedRef.current = null;
            }
            profileCloseRequestRef.current = null;
            profileClosingRef.current = false;
            return;
        }
        // Тот же модуль уже смонтирован в этом хосте (смена вкладки, открытие/закрытие модалки,
        // смена темы/локали меняют ССЫЛКУ profileCtx, но не сам продукт-владелец) — толкаем свежий
        // ctx через updateCtx (тот же контракт, что ModuleHost.tsx), без ремонта корня. Раньше эффект
        // на такую смену ctx молча создавал ВТОРОЙ пейн, не снимая первый (unmount ниже отрабатывал
        // только при закрытии) — невидимая копия ящика профиля перехватывала клики (B41).
        const mounted = profileMountedRef.current;
        if (mounted && mounted.module === profileCtx.module && mounted.mod.updateCtx) {
            try {
                mounted.mod.updateCtx(mounted.el, profileCtx);
            }
            catch { /* необязательный шов */ }
            return;
        }
        let cancelled = false;
        (async () => {
            let mod;
            try {
                mod = await loadModule(profileCtx.module);
            }
            catch {
                if (!cancelled)
                    setProfileOpen(false);
                return;
            }
            if (cancelled)
                return;
            // Продукт без updateCtx (или смена продукта-владельца) — старый пейн снимаем ПЕРЕД
            // созданием нового, иначе оба остаются в DOM одновременно (та же причина, что выше).
            const stale = profileMountedRef.current;
            if (stale) {
                try {
                    stale.mod.unmount(stale.el);
                }
                catch { /* игнор */ }
                stale.el.remove();
                profileMountedRef.current = null;
            }
            const pane = document.createElement("div");
            pane.className = "profile-overlay-pane";
            host.appendChild(pane);
            try {
                mod.mount(pane, profileCtx);
            }
            catch {
                pane.remove();
                return;
            }
            profileMountedRef.current = { el: pane, mod, module: profileCtx.module };
        })();
        return () => { cancelled = true; };
    }, [profileOpen, profileCtx]);
    return { profileOpen, profileClosing, profileTab, profileHostRef, profileCtx, requestProfileClose, openProfile };
}
